/**
 * Expandly AI Portal Design System
 * Colors, typography, spacing, and component styles
 * Supports Light and Dark mode
 */

// Light theme colors
export const lightColors = {
  primary: '#4A90E2',
  primaryLight: '#6BA5E7',
  primaryDark: '#357ABD',

  success: '#50C878',
  successLight: '#6FD38E',
  successDark: '#3FA861',

  warning: '#FF6B35',
  warningLight: '#FF8555',
  warningDark: '#E54E1F',

  purple: '#7B68EE',
  purpleLight: '#9584F2',
  purpleDark: '#5E4CC4',

  error: '#EF4444',
  info: '#4A90E2',

  // Neutral colors
  white: '#FFFFFF',
  black: '#000000',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  gray900: '#111827',

  background: '#F5F7FA',
  backgroundSecondary: '#FFFFFF',
  border: '#E9EDF2',
  textPrimary: '#1F2937',
  textSecondary: '#6B7280',
  cardBackground: '#FFFFFF',
}

// Dark theme colors
export const darkColors = {
  primary: '#6BA5E7',
  primaryLight: '#8ABDEF',
  primaryDark: '#4A90E2',

  success: '#6FD38E',
  successLight: '#8FDFA7',
  successDark: '#50C878',

  warning: '#FF8555',
  warningLight: '#FFA07A',
  warningDark: '#FF6B35',

  purple: '#9584F2',
  purpleLight: '#B0A3F7',
  purpleDark: '#7B68EE',

  error: '#F87171',
  info: '#6BA5E7',

  // Neutral colors (inverted)
  white: '#1F2937',
  black: '#FFFFFF',
  gray50: '#111827',
  gray100: '#1F2937',
  gray200: '#374151',
  gray300: '#4B5563',
  gray400: '#6B7280',
  gray500: '#9CA3AF',
  gray600: '#D1D5DB',
  gray700: '#E5E7EB',
  gray800: '#F3F4F6',
  gray900: '#F9FAFB',

  background: '#0F172A',
  backgroundSecondary: '#1E293B',
  border: '#334155',
  textPrimary: '#F1F5F9',
  textSecondary: '#94A3B8',
  cardBackground: '#1E293B',
}

export const theme = {
  // Brand Colors (from UIBRIEF.md) - defaults to light mode
  colors: lightColors,

  // AI-specific
  ai: {
    gradient: 'linear-gradient(135deg, #7B68EE 0%, #9584F2 100%)',
    border: '#7B68EE',
    glow: '0 0 20px rgba(123, 104, 238, 0.3), 0 4px 6px rgba(0, 0, 0, 0.1)',
  },

  // Typography
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontMono: '"JetBrains Mono", "Fira Code", "Courier New", monospace',

    fontSize: {
      xs: '12px',
      sm: '14px',
      base: '16px',
      lg: '18px',
      xl: '20px',
      '2xl': '24px',
      '3xl': '30px',
      '4xl': '36px',
      '5xl': '48px',
    },

    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },

  // Spacing (8px base unit)
  spacing: {
    0: '0',
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    8: '32px',
    10: '40px',
    12: '48px',
    16: '64px',
    20: '80px',
    24: '96px',
  },

  // Border radius
  borderRadius: {
    none: '0',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    '2xl': '24px',
    full: '9999px',
  },

  // Shadows
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    ai: '0 0 20px rgba(123, 104, 238, 0.3), 0 4px 6px rgba(0, 0, 0, 0.1)',
  },

  // Breakpoints
  breakpoints: {
    xs: '320px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1440px',
  },

  // Z-index
  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1100,
    modal: 1200,
    popover: 1300,
    toast: 1400,
  },

  // Transitions
  transitions: {
    fast: '150ms ease-in-out',
    base: '300ms ease-in-out',
    slow: '500ms ease-in-out',
  },
}

export default theme
